// Copyright (c) Docugami, Inc. All rights reserved.**/

import { dgFontFamily } from "components/common/Fonts";
import { appBackgroundGrey, emptyGray } from "components/shell/presentation/AppStyles";
import { ReportViewMode, } from "hooks/navigationService";
import { useAppSelector } from "hooks/redux";
import { ReportViewContext } from "networking/reportsApi";
import { selectClusterId, selectIsReportPublished, selectReport, selectReportContext, selectReportLabels, selectReportViewMode } from "store/reports/selectors";
import styled, { css } from "styled-components";
import { DocumentPicker } from "./DocumentPicker";
import { ReportTableFilters } from "./ReportTableFilters";
import { ModelAccuracyBar } from "components/reports/ModelAccuracyBar";

const dividerStyling = css`& > *:not(:first-child) {
    border-left: 1px solid ${emptyGray};
    padding-left: 10px;
    margin-left: 10px;
    height: 100%;
}`

export const StepHeaderContainer = styled.div`
    display: flex;
    height: 80px;
    background: ${appBackgroundGrey};
    padding: 10px;
    box-sizing: border-box;
    border-bottom: 1px solid ${emptyGray};
    justify-content: space-between;
    align-items: center;
    text-align: center;
`

export const StepLabel = styled.div`
    font-family: ${dgFontFamily};
    font-size: 28px;
    text-transform: uppercase;
    display: flex;
    margin-right: auto;
    margin-left: 10px;
    align-items: center;
    padding: 0 10px;
`

export const StepHeaderSection = styled.div<{ justifyContent?: string, addDividerStyling?: boolean }>`
    flex: 1;
    display: flex;
    height: 100%;
    ${props => props.addDividerStyling ? dividerStyling : ''}
    justify-content: ${props => !!props.justifyContent ? props.justifyContent : 'inherit'};
    ${props => props.hidden && 'display: none'};
`

const FiltersContainer = styled(StepHeaderSection)`
    align-items: center;
    gap: 10px;
`;

export const StepHeader = () => {
    const viewMode = useAppSelector(selectReportViewMode);
    const reportLabels = useAppSelector(selectReportLabels);
    const context = useAppSelector(selectReportContext);
    const clusterId = useAppSelector(selectClusterId);
    const isPublished = useAppSelector(selectIsReportPublished);
    const autoPublish = !!useAppSelector(selectReport)?.autoPublishDocuments;
    const docSetName = useAppSelector(state => state.clusters.indexed[clusterId]?.clusterInfo.label);

    function getTitle() {
        if (context === ReportViewContext.Review) {
            return isPublished ? 'manual confirmation' : `${reportLabels.titleName.toLocaleLowerCase()} designer`
        }
        else if (context === ReportViewContext.Published) {
            return `published ${reportLabels.titleName.toLocaleLowerCase()}`
        }
    }

    const HeaderPrefix = () => (
        <StepHeaderSection addDividerStyling>
            <StepLabel>
                {getTitle()}
            </StepLabel>
        </StepHeaderSection>
    );

    switch (viewMode) {
        case ReportViewMode.Abstract:
            return (
                <StepHeaderContainer>
                    <HeaderPrefix />
                    <StepHeaderSection justifyContent="flex-end">
                        <DocumentPicker />
                    </StepHeaderSection>
                </StepHeaderContainer>
            );
        case ReportViewMode.Document:
            return (
                <StepHeaderContainer >
                    <StepHeaderSection addDividerStyling justifyContent="flex-end">
                        <HeaderPrefix />
                        <DocumentPicker />
                    </StepHeaderSection>
                </StepHeaderContainer>
            );
        case ReportViewMode.Table:
            return (
                <StepHeaderContainer>
                    <StepHeaderSection addDividerStyling justifyContent="flex-end">
                        <HeaderPrefix />
                        {
                            (!isPublished || autoPublish) && (
                                <FiltersContainer justifyContent="flex-start" style={{ flex: 2 }}>
                                    <ReportTableFilters />
                                </FiltersContainer>
                            )
                        }
                        <ModelAccuracyBar docsetId={clusterId} />
                    </StepHeaderSection>
                </StepHeaderContainer>
            );
        case ReportViewMode.Chat:
            return (
                <StepHeaderContainer>
                    <StepHeaderSection addDividerStyling justifyContent="flex-end">
                        <StepHeaderSection addDividerStyling>
                            <StepLabel>{!!docSetName ? `Chat with ${docSetName}` : `Chat`}</StepLabel>
                        </StepHeaderSection>
                    </StepHeaderSection>
                </StepHeaderContainer>
            );
    }
}
